import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import DataTable from 'src/components/ui/data-table';
import { StatusBadge } from 'src/components/common/StatusBadge';
import {
  Info,
  FileText,
  Building,
  AlertCircle,
  CheckCircle,
  Clock,
  IndianRupee,
  Plus,
} from 'lucide-react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { useToast } from 'src/hooks/use-toast';
import BillingTabs from 'src/pages/Billing/BillingTabs/BillingTabs';
import StatsSection from 'src/components/common/StatsSection';
import {
  useCreditNoteList,
  useBillingSummary,
} from 'src/services/api/queries/billingQueries';
import { useCreateCreditNote } from 'src/services/api/mutations/billingMutations';
import { CreateCreditNoteModal } from 'src/components/Modals/CreateCreditNoteModal';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from 'src/components/ui/tooltip';

const DEFAULT_PAGE_SIZE = 10;

const formatCompact = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (abs >= 1_00_00_000)
    return `${sign}${(abs / 1_00_00_000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (abs >= 1_00_000)
    return `${sign}${(abs / 1_00_000).toFixed(2).replace(/\.?0+$/, '')}L`;
  if (abs >= 1_000)
    return `${sign}${(abs / 1_000).toFixed(2).replace(/\.?0+$/, '')}K`;
  return `${sign}${abs.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const AmountCell = ({ value, currency }) => (
  <div className="flex items-center">
    {currency} {value}
  </div>
);

const OrganizationCell = ({ value, row }) => {
  const customerCategory = row?.customer_category;

  if (!customerCategory) {
    return (
      <div className="flex items-center">
        <Building className="w-4 h-4 mr-2" />
        {value}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <div className="flex items-center cursor-pointer">
            <Building className="w-4 h-4 mr-2" />
            {value}
          </div>
        </TooltipTrigger>
        <TooltipContent>{customerCategory}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const getColumns = () => [
  {
    key: 'credit_note_number',
    header: 'ID',
  },
  {
    key: 'organization_name',
    header: 'Customer',
    render: (value, row) => <OrganizationCell value={value} row={row} />,
  },
  {
    key: 'total_amount',
    header: (
      <div className="flex items-center gap-1">
        Total Amount
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="p-0 m-0 bg-transparent border-0 cursor-pointer"
                aria-label="Info: GST inclusive"
                tabIndex={0}
              >
                <Info className="w-4 h-4 text-white-500" />
              </button>
            </TooltipTrigger>
            <TooltipContent>GST inclusive</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
    render: (value, row) => (
      <AmountCell value={value} currency={row.currency} />
    ),
  },
  {
    key: 'reason',
    header: 'Reason',
  },
  {
    key: 'status',
    header: 'Status',
    render: (value) => <StatusBadge value={value} />,
  },
  {
    key: 'issued_date',
    header: 'Issued',
    render: (value) => {
      const val = value;
      if (!val) return '';
      const date = new Date(val);
      if (Number.isNaN(date)) return val; // fallback if invalid date
      const day = String(date.getDate()).padStart(2, '0');
      const month = date
        .toLocaleString('en-US', { month: 'short' })
        .toUpperCase();
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
  },
];

function ListCreditNotes() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const createCreditNoteMutation = useCreateCreditNote();

  // Extract params from URL
  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );
  // You may want to get these from filters/UI in the future
  const status = searchParams.get('status') || 'ISSUED';
  // const organizationId = searchParams.get('organizationId') || '2';

  // Fetch credit notes using react-query
  const {
    data: response,
    isLoading,
    isError,
    error,
  } = useCreditNoteList({ page, pageSize, status });
  // Real totals/status-breakdown across the whole dataset, not just the
  // current page — see BillingService.getBillingSummary.
  const { data: summaryData } = useBillingSummary('credit_note');
  const creditNoteSummary = summaryData?.data?.credit_note ?? {};

  // Extract items and pagination from API response
  const items = response?.data?.items || [];
  const pagination = response?.data?.pagination || {
    page: 1,
    limit: pageSize,
    total: 0,
    pages: 1,
  };
  const metrics = response?.data?.metrics ?? null;

  const updateUrlParams = (params) => {
    const updatedParams = new URLSearchParams(searchParams);
    if (params.page) updatedParams.set('page', params.page.toString());
    if (params.pageSize)
      updatedParams.set('pageSize', params.pageSize.toString());
    if (params.status) updatedParams.set('status', params.status);
    if (params.organizationId)
      updatedParams.set('organizationId', params.organizationId);
    setSearchParams(updatedParams);
  };

  const handlePageChange = (newPage) => {
    const tempPage = parseInt(newPage, 10);
    updateUrlParams({ page: tempPage });
  };

  const handlePageSizeChange = (newPageSize) => {
    const size = parseInt(newPageSize, 10);
    updateUrlParams({ pageSize: size, page: 1 });
  };

  React.useEffect(() => {
    if (!searchParams.has('pageSize')) {
      updateUrlParams({ pageSize: DEFAULT_PAGE_SIZE });
    }
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (isError) {
      toast({
        title: 'Error',
        description:
          error?.message ||
          'Failed to load credit notes. Please refresh the page.',
        variant: 'destructive',
      });
    }
    // eslint-disable-next-line
  }, [isError, error]);

  const handleCreateSubmit = async (payload) => {
    try {
      await createCreditNoteMutation.mutateAsync(payload);
      toast({
        title: 'Success',
        description: 'Credit note created successfully.',
        variant: 'success',
      });
      setIsCreateModalOpen(false);
    } catch (err) {
      toast({
        title: 'Error',
        description: err?.message || 'Failed to create credit note.',
        variant: 'destructive',
      });
    }
  };

  const columns = getColumns();

  const draftCount = creditNoteSummary.status_breakdown?.DRAFT ?? 0;
  const issuedCount =
    (creditNoteSummary.status_breakdown?.ISSUED ?? 0) +
    (creditNoteSummary.status_breakdown?.APPROVED ?? 0);
  const totalValue = creditNoteSummary.total_amount ?? 0;

  return (
    <div>
      <PageHeader
        title="Credit Notes"
        actions={
          <Button
            variant="outline"
            className="gap-1.5"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            Create Credit Note
          </Button>
        }
      />
      <PageContainer>
        <StatsSection
          title="Summary"
          className="mb-5"
          cards={[
            {
              key: 'total',
              label: 'Total Credit Notes',
              value: creditNoteSummary.total_count ?? pagination.total ?? 0,
              iconColor: 'var(--primary)',
              icon: (
                <FileText style={{ width: 20, height: 20, strokeWidth: 1.8 }} />
              ),
            },
            {
              key: 'draft',
              label: 'Draft',
              value: draftCount,
              iconColor: '#B45309',
              icon: (
                <Clock style={{ width: 20, height: 20, strokeWidth: 1.8 }} />
              ),
            },
            {
              key: 'issued',
              label: 'Issued',
              value: issuedCount,
              iconColor: '#0D9668',
              icon: (
                <CheckCircle
                  style={{ width: 20, height: 20, strokeWidth: 1.8 }}
                />
              ),
            },
            {
              key: 'value',
              label: 'Total Value',
              value: formatCompact(totalValue),
              iconColor: '#7C3AED',
              icon: (
                <IndianRupee
                  style={{ width: 20, height: 20, strokeWidth: 1.8 }}
                />
              ),
            },
          ]}
        />

        <BillingTabs metrics={metrics} />

        {isError ? (
          <div className="flex flex-col items-center justify-center py-16 px-5 text-center bg-card rounded-xl border border-border">
            <div
              className="flex items-center justify-center mb-5 rounded-full bg-ds-danger-subtle"
              style={{ width: 72, height: 72 }}
            >
              <AlertCircle
                className="text-ds-danger"
                style={{ width: 32, height: 32, strokeWidth: 1.5 }}
              />
            </div>
            <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-[-0.01em]">
              Failed to load credit notes
            </p>
            <p className="text-[13px] text-muted-foreground leading-relaxed max-w-xs mb-4">
              {error?.message ||
                'An error occurred while fetching credit notes.'}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.reload()}
            >
              Try again
            </Button>
          </div>
        ) : (
          <DataTable
            data={items}
            columns={columns}
            loading={isLoading}
            rowKey="id"
            hover
            stickyHeader
            onRowClick={(row) => navigate(`/credit-notes/${row?.id}`)}
            pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            pageSize={pagination.limit}
            totalItems={pagination.total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            className="border-border shadow-[0_1px_3px_rgba(0,0,0,.04)]"
            emptyState={
              <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
                <div
                  className="flex items-center justify-center mb-5 rounded-full bg-border"
                  style={{ width: 72, height: 72 }}
                >
                  <FileText
                    className="text-muted-foreground"
                    style={{ width: 32, height: 32, strokeWidth: 1.5 }}
                  />
                </div>
                <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-[-0.01em]">
                  No credit notes found
                </p>
                <p className="text-[13px] text-muted-foreground leading-relaxed max-w-xs mb-5">
                  Credit notes will appear here once they are generated.
                </p>
                <Button
                  variant="outline"
                  className="gap-1.5"
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Create Credit Note
                </Button>
              </div>
            }
          />
        )}
      </PageContainer>

      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Create Credit Note</DialogTitle>
            <DialogDescription>
              Select a customer and candidate to issue a credit note against.
            </DialogDescription>
          </DialogHeader>
          <CreateCreditNoteModal
            onSubmit={handleCreateSubmit}
            onCancel={() => setIsCreateModalOpen(false)}
            loading={createCreditNoteMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ListCreditNotes;
